<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide details about the entity or corporate account. This information is
          required by your advisor and the custodian to complete the account setup.
        </p>
      </div>
    </help-drawer>

    <v-form name="EntityAccount" class="form" data-test-id="entity-account-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.ENTITY_ACCOUNT.id" :instance="instance" />

        <!-- Heading -->
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="entity-account-header">
              <span class="headerlight">
                {{ accountName }} <strong>Entity Account Details</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>

        <!-- Sync message -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving entity account information...</p>
        </div>

        <!-- Re-open form message -->
        <data-submitted-message 
          v-if="isEntityAccountComplete(instance) && formHidden && !isSyncing" 
          :submitted="isEntityAccountComplete(instance)"
          v-on:hide-form="toggleFormState" 
          data-test-id="data-submitted-message"
        />

        <!-- === FORM === -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container">
              <!-- Entity Name -->
              <label for="entityName" class="required" data-test-id="entity-name-label">
                Full Legal Name of Entity
              </label>
              <v-text-field
                outlined
                type="text"
                maxlength="256"
                name="entityName"
                aria-required="true"
                v-model.trim="entityName"
                :backgroundColor="inputBackgroundColor"
                @input="$v.entityName.$touch()"
                @blur="$v.entityName.$touch()"
                :error-messages="entityNameErrors"
                hide-details="auto"
                class="mb-2"
                placeholder="Example: ABC Corporation"
                data-test-id="entity-name-input"
              />

              <!-- Entity Type -->
              <label for="entityType" class="required" data-test-id="entity-type-label">
                Type of Entity
              </label>
              <v-select
                v-model="entityType"
                :items="entityTypeOptions"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Choose Entity Type"
                dense
                hide-details="auto"
                @change="$v.entityType.$touch()"
                :error-messages="entityTypeErrors"
                class="mb-2"
                data-test-id="entity-type-select"
              />

              <!-- State of Formation -->
              <label for="stateOfFormation" class="required" data-test-id="state-formation-label">
                State of Formation/Incorporation
              </label>
              <v-select
                v-model="stateOfFormation"
                :items="states"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Choose State"
                dense
                hide-details="auto"
                @change="$v.stateOfFormation.$touch()"
                :error-messages="stateOfFormationErrors"
                class="mb-2"
                data-test-id="state-formation-select"
              />

              <!-- Date of Formation -->
              <label for="formationDate" class="required" data-test-id="formation-date-label">
                Date of Formation/Incorporation
              </label>
              <v-text-field
                v-model="formationDate"
                :placeholder="INPUT_DATE_FMT"
                outlined
                name="formationDate"
                maxlength="8"
                aria-required="true"
                :backgroundColor="inputBackgroundColor"
                @input="$v.formationDate.$touch()"
                @blur="standardizeFormationDate()"
                :error-messages="formationDateErrors"
                hide-details="auto"
                inputmode="numeric"
                class="mb-2"
                data-test-id="formation-date-input"
              >
                <template v-slot:append>
                  <span class="cal-icon" data-test-id="calendar-icon">
                    <v-icon small color="#9b9b9b">fas fa-calendar-alt</v-icon>
                  </span>
                </template>
              </v-text-field>

              <!-- Tax Information -->
              <h3 class="section-header mt-4 mb-2">Tax Information</h3>
              
              <!-- EIN -->
              <label for="ein" class="required" data-test-id="ein-label">
                Employer Identification Number (EIN)
              </label>
              <v-text-field
                outlined
                type="text"
                maxlength="10"
                name="ein"
                aria-required="true"
                v-model.trim="ein"
                :backgroundColor="inputBackgroundColor"
                @input="formatEIN"
                @blur="$v.ein.$touch()"
                :error-messages="einErrors"
                hide-details="auto"
                placeholder="12-3456789"
                inputmode="numeric"
                class="mb-2"
                data-test-id="ein-input"
              />

              <!-- Authorized Signers -->
              <h3 class="section-header mt-4 mb-2">Authorized Signers</h3>
              
              <v-row>
                <v-col>
                  <label for="signerFirstName" class="required" data-test-id="signer-first-name-label">
                    Authorized Signer First Name
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="signerFirstName"
                    aria-required="true"
                    v-model.trim="signerFirstName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.signerFirstName.$touch()"
                    @blur="$v.signerFirstName.$touch()"
                    :error-messages="signerFirstNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="signer-first-name-input"
                  />
                </v-col>
                <v-col>
                  <label for="signerLastName" class="required" data-test-id="signer-last-name-label">
                    Authorized Signer Last Name
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="signerLastName"
                    aria-required="true"
                    v-model.trim="signerLastName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.signerLastName.$touch()"
                    @blur="$v.signerLastName.$touch()"
                    :error-messages="signerLastNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="signer-last-name-input"
                  />
                </v-col>
              </v-row>

              <label for="signerTitle" class="required" data-test-id="signer-title-label">
                Title/Position
              </label>
              <v-text-field
                outlined
                type="text"
                maxlength="256"
                name="signerTitle"
                aria-required="true"
                v-model.trim="signerTitle"
                :backgroundColor="inputBackgroundColor"
                @input="$v.signerTitle.$touch()"
                @blur="$v.signerTitle.$touch()"
                :error-messages="signerTitleErrors"
                hide-details="auto"
                placeholder="Example: President, CEO, Managing Member"
                class="mb-2"
                data-test-id="signer-title-input"
              />

              <!-- Additional Signers (Optional) -->
              <v-checkbox
                hide-details
                :ripple="false"
                v-model="hasAdditionalSigner"
                :color="primaryColor"
                @change="additionalSignerChanged()"
                class="mt-2 mb-4"
                data-test-id="additional-signer-checkbox"
              >
                <template v-slot:label>
                  <span class="cb-label">Add Additional Authorized Signer</span>
                </template>
              </v-checkbox>

              <div v-if="hasAdditionalSigner">
                <v-row>
                  <v-col>
                    <label for="additionalSignerFirstName" class="required" data-test-id="additional-signer-first-name-label">
                      Additional Signer First Name
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="256"
                      name="additionalSignerFirstName"
                      aria-required="true"
                      v-model.trim="additionalSignerFirstName"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.additionalSignerFirstName.$touch()"
                      @blur="$v.additionalSignerFirstName.$touch()"
                      :error-messages="additionalSignerFirstNameErrors"
                      hide-details="auto"
                      class="mb-2"
                      data-test-id="additional-signer-first-name-input"
                    />
                  </v-col>
                  <v-col>
                    <label for="additionalSignerLastName" class="required" data-test-id="additional-signer-last-name-label">
                      Additional Signer Last Name
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="256"
                      name="additionalSignerLastName"
                      aria-required="true"
                      v-model.trim="additionalSignerLastName"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.additionalSignerLastName.$touch()"
                      @blur="$v.additionalSignerLastName.$touch()"
                      :error-messages="additionalSignerLastNameErrors"
                      hide-details="auto"
                      class="mb-2"
                      data-test-id="additional-signer-last-name-input"
                    />
                  </v-col>
                </v-row>

                <label for="additionalSignerTitle" class="required" data-test-id="additional-signer-title-label">
                  Title/Position
                </label>
                <v-text-field
                  outlined
                  type="text"
                  maxlength="256"
                  name="additionalSignerTitle"
                  aria-required="true"
                  v-model.trim="additionalSignerTitle"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.additionalSignerTitle.$touch()"
                  @blur="$v.additionalSignerTitle.$touch()"
                  :error-messages="additionalSignerTitleErrors"
                  hide-details="auto"
                  placeholder="Example: Vice President, CFO, Member"
                  class="mb-2"
                  data-test-id="additional-signer-title-input"
                />
              </div>

              <!-- Business Purpose -->
              <label for="businessPurpose" class="required mt-4" data-test-id="business-purpose-label">
                Primary Business Purpose
              </label>
              <v-textarea
                outlined
                name="businessPurpose"
                aria-required="true"
                v-model.trim="businessPurpose"
                :backgroundColor="inputBackgroundColor"
                @input="$v.businessPurpose.$touch()"
                @blur="$v.businessPurpose.$touch()"
                :error-messages="businessPurposeErrors"
                hide-details="auto"
                rows="3"
                placeholder="Briefly describe the entity's primary business activities"
                class="mb-2"
                data-test-id="business-purpose-input"
              />

              <!-- Skip for now -->
              <div class="skip-for-now">
                <a @click="skip()" data-test-id="skip-button">Skip for now</a>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-form>

    <bottom-nav @go-prev="prev()" @go-next="next()" data-test-id="entity-account-nav" />
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import {
  MENUS,
  INPUT_DATE_FMT,
  US_DATE_SHORT,
  ISO_DATE_SHORT,
  validInputDate,
  DB_ACCOUNT_TYPES,
  STATES,
} from '@/common/constants';
import { required, requiredIf } from 'vuelidate/lib/validators';

// Custom validator for EIN format
const validateEIN = (value) => {
  if (!value) return true; // Let required validator handle empty
  const cleanEIN = value.replace(/[^0-9]/g, '');
  return cleanEIN.length === 9;
};

export default {
  name: 'EntityAccount',
  props: {
    name: String,
    label: String,
  },
  components: {
    HelpDrawer,
    BottomNav,
    DataSubmittedMessage,
    SyncingIndicator,
  },
  mixins: [InterviewClientMixin],

  data() {
    return {
      MENUS,
      INPUT_DATE_FMT,

      /* form fields */
      entityName: null,
      entityType: null,
      stateOfFormation: null,
      formationDate: null,
      ein: null,
      signerFirstName: null,
      signerLastName: null,
      signerTitle: null,
      hasAdditionalSigner: false,
      additionalSignerFirstName: null,
      additionalSignerLastName: null,
      additionalSignerTitle: null,
      businessPurpose: null,

      /* Options */
      entityTypeOptions: [
        'C Corporation',
        'S Corporation',
        'Limited Liability Company (LLC)',
        'Limited Partnership (LP)',
        'Limited Liability Partnership (LLP)',
        'General Partnership',
        'Sole Proprietorship',
        'Non-Profit Organization',
        'Professional Corporation',
        'Other',
      ],
      states: STATES,
    };
  },

  validations() {
    return {
      entityName: { required },
      entityType: { required },
      stateOfFormation: { required },
      formationDate: { required, validInputDate },
      ein: { required, validateEIN },
      signerFirstName: { required },
      signerLastName: { required },
      signerTitle: { required },
      additionalSignerFirstName: {
        required: requiredIf(() => this.hasAdditionalSigner),
      },
      additionalSignerLastName: {
        required: requiredIf(() => this.hasAdditionalSigner),
      },
      additionalSignerTitle: {
        required: requiredIf(() => this.hasAdditionalSigner),
      },
      businessPurpose: { required },
      validationGroup: [
        'entityName',
        'entityType',
        'stateOfFormation',
        'formationDate',
        'ein',
        'signerFirstName',
        'signerLastName',
        'signerTitle',
        'additionalSignerFirstName',
        'additionalSignerLastName',
        'additionalSignerTitle',
        'businessPurpose',
      ],
    };
  },

  computed: {
    /* Dynamic error messages */
    entityNameErrors() {
      const errors = [];
      if (!this.$v.entityName.$dirty) return errors;
      !this.$v.entityName.required && errors.push('Entity name is required');
      return errors;
    },
    entityTypeErrors() {
      const errors = [];
      if (!this.$v.entityType.$dirty) return errors;
      !this.$v.entityType.required && errors.push('Entity type is required');
      return errors;
    },
    stateOfFormationErrors() {
      const errors = [];
      if (!this.$v.stateOfFormation.$dirty) return errors;
      !this.$v.stateOfFormation.required && errors.push('State of formation is required');
      return errors;
    },
    formationDateErrors() {
      const errors = [];
      if (!this.$v.formationDate.$dirty) return errors;
      !this.$v.formationDate.required && errors.push('Formation date is required');
      !this.$v.formationDate.validInputDate && errors.push('Invalid date (MMDDYYYY)');
      return errors;
    },
    einErrors() {
      const errors = [];
      if (!this.$v.ein.$dirty) return errors;
      !this.$v.ein.required && errors.push('EIN is required');
      !this.$v.ein.validateEIN && errors.push('EIN must be 9 digits (XX-XXXXXXX)');
      return errors;
    },
    signerFirstNameErrors() {
      const errors = [];
      if (!this.$v.signerFirstName.$dirty) return errors;
      !this.$v.signerFirstName.required && errors.push('First name is required');
      return errors;
    },
    signerLastNameErrors() {
      const errors = [];
      if (!this.$v.signerLastName.$dirty) return errors;
      !this.$v.signerLastName.required && errors.push('Last name is required');
      return errors;
    },
    signerTitleErrors() {
      const errors = [];
      if (!this.$v.signerTitle.$dirty) return errors;
      !this.$v.signerTitle.required && errors.push('Title is required');
      return errors;
    },
    additionalSignerFirstNameErrors() {
      const errors = [];
      if (!this.$v.additionalSignerFirstName.$dirty) return errors;
      !this.$v.additionalSignerFirstName.required && errors.push('First name is required');
      return errors;
    },
    additionalSignerLastNameErrors() {
      const errors = [];
      if (!this.$v.additionalSignerLastName.$dirty) return errors;
      !this.$v.additionalSignerLastName.required && errors.push('Last name is required');
      return errors;
    },
    additionalSignerTitleErrors() {
      const errors = [];
      if (!this.$v.additionalSignerTitle.$dirty) return errors;
      !this.$v.additionalSignerTitle.required && errors.push('Title is required');
      return errors;
    },
    businessPurposeErrors() {
      const errors = [];
      if (!this.$v.businessPurpose.$dirty) return errors;
      !this.$v.businessPurpose.required && errors.push('Business purpose is required');
      return errors;
    },
    
    /* Helpers */
    helpTitle() {
      return 'Why do we need this information?';
    },
    accountName() {
      // Map account type to display name
      if (this.name === DB_ACCOUNT_TYPES.OFFLINE_CORPORATE) {
        return 'Corporate';
      }
      return this.name;
    },
    instance() {
      return {
        name: this.name,
        label: this.label
      };
    },
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.ENTITY_ACCOUNT.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    isEntityAccountCompleteForInstance() {
      return this.isEntityAccountComplete(this.instance);
    },
  },

  watch: {
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    isEntityAccountCompleteForInstance(newVal) {
      if (newVal) {
        this.formHidden = true;
      }
    },
  },

  methods: {
    /* Date formatter */
    standardizeFormationDate() {
      this.$v.formationDate.$touch();
      if (validInputDate(this.formationDate)) {
        this.formationDate = window
          .moment(this.formationDate, INPUT_DATE_FMT, true)
          .format(US_DATE_SHORT);
      }
    },

    /* EIN formatter */
    formatEIN() {
      // Remove all non-digits
      let value = this.ein.replace(/[^0-9]/g, '');
      
      // Add hyphen after first 2 digits
      if (value.length > 2) {
        value = value.slice(0, 2) + '-' + value.slice(2, 9);
      }
      
      this.ein = value;
      this.$v.ein.$touch();
    },

    /* Additional signer checkbox handler */
    additionalSignerChanged() {
      if (!this.hasAdditionalSigner) {
        // Clear additional signer fields when unchecked
        this.additionalSignerFirstName = null;
        this.additionalSignerLastName = null;
        this.additionalSignerTitle = null;
        this.$v.additionalSignerFirstName.$reset();
        this.$v.additionalSignerLastName.$reset();
        this.$v.additionalSignerTitle.$reset();
      }
    },

    /* Navigation */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      /* Prepare payload */
      const data = {
        instance: this.instance,
        entityName: this.entityName,
        entityType: this.entityType,
        stateOfFormation: this.stateOfFormation,
        formationDate: this.formationDate
          ? window.moment(this.formationDate, US_DATE_SHORT, true).format(ISO_DATE_SHORT)
          : null,
        ein: this.ein.replace(/-/g, ''), // Remove hyphen for storage
        signerFirstName: this.signerFirstName,
        signerLastName: this.signerLastName,
        signerTitle: this.signerTitle,
        businessPurpose: this.businessPurpose,
      };

      // Include additional signer if present
      if (this.hasAdditionalSigner) {
        data.hasAdditionalSigner = true;
        data.additionalSignerFirstName = this.additionalSignerFirstName;
        data.additionalSignerLastName = this.additionalSignerLastName;
        data.additionalSignerTitle = this.additionalSignerTitle;
      }

      const pageName = MENUS.CLIENT.ENTITY_ACCOUNT.id;

      /* Mark page as syncing immediately for better user feedback */
      this.markPageAsSyncing(pageName, this.instance);

      this.completeStep(pageName, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => { });
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },

    /* Toggle form visibility */
    toggleFormState(hide) {
      this.formHidden = hide;
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },

    /* Handle sync status changes */
    handleSyncStatusChange() {
      if (this.isEntityAccountComplete(this.instance)) {
        this.formHidden = true;
      }
    },

    /* Check if Entity Account page is complete */
    isEntityAccountComplete(instance) {
      if (!this.clientProgress || !this.clientProgress.progress) return false;

      const page = this.clientProgress.progress.find(
        (p) =>
          p.pageName === MENUS.CLIENT.ENTITY_ACCOUNT.id &&
          p.instance === instance.name && 
          p.label === instance.label
      );

      return page ? page.lastCompleted !== null && page.status === 'SYNCED' : false;
    },
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();

    // Determine the current page index based on the pathname
    this.pageIndex = undefined;
    let i = 0;
    for (const page of this.pages) {
      if (
        typeof page === 'object' &&
        page.pathname &&
        window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))
      ) {
        this.pageIndex = i;
        break;
      }
      i++;
    }

    // Safety fallback if index still undefined
    if (this.pageIndex === undefined) {
      this.pageIndex = this.pages.findIndex((p) => 
        (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.ENTITY_ACCOUNT.id
      );
    }

    this.formHidden = this.isEntityAccountCompleteForInstance;
  },

  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      if (this.isEntityAccountComplete(this.instance)) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  },
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;

  p {
    color: #666;
    font-style: italic;
  }
}

.section-header {
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 12px;
}

.cal-icon {
  line-height: 32px;
  margin-right: 8px;
}

.cb-label {
  font-size: 16px;
  color: var(--color-text);
}

/* Override checkbox styles for consistency */
.v-input--checkbox {
  margin-top: 0;
}

/* Textarea styling */
.v-textarea {
  margin-top: 8px;
}
</style>